import {
  APP_SMART_LINK_URL,
  APP_STORE_URL,
  PLAY_STORE_URL,
} from '@/constants';

export type Platform = 'ios' | 'android' | 'other';

/**
 * Which mobile platform this is, for routing someone to the right app store
 * (GIFTWEB-27, GIFTWEB-30).
 *
 * Read from the user agent rather than `navigator.userAgentData`, whose
 * `platform` is a high-entropy hint: Chromium-only and asynchronous, so it
 * cannot answer a render.
 *
 * Arguments are injectable so the real strings can be tested.
 */
export function detectPlatform(
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  maxTouchPoints: number = typeof navigator === 'undefined'
    ? 0
    : (navigator.maxTouchPoints ?? 0),
): Platform {
  if (!userAgent) return 'other';

  // Android first: its user agent also says "Linux", which nothing else here
  // should be allowed to claim.
  if (/Android/i.test(userAgent)) return 'android';

  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';

  // iPadOS 13 and later report themselves as a Mac, so the only tell is that
  // Macs are not touchscreens. Deliberately narrowed to Macintosh: touchscreen
  // Windows laptops are common and are not iPads.
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return 'ios';

  return 'other';
}

/**
 * Where to send someone to install the app. Desktop and anything unrecognised
 * gets the smart link, which resolves per device at the other end.
 */
export function storeUrlFor(platform: Platform): string {
  if (platform === 'ios') return APP_STORE_URL;
  if (platform === 'android') return PLAY_STORE_URL;
  return APP_SMART_LINK_URL;
}
